import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone,
  Zap,
  TrendingUp,
  Smile,
  Activity,
  Clock,
  AlertTriangle,
  Bot,
} from 'lucide-react';
import { GlassCard, KPICard } from '../components/ui/GlassCard';
import { AgentGraph } from '../components/agents/AgentGraph';
import { CallStatusPanel } from '../components/calls/CallStatusPanel';
import { ToolTimeline } from '../components/timeline/ToolTimeline';
import { LiveTranscript } from '../components/transcript/LiveTranscript';
import { WaveformVisualizer } from '../components/audio/WaveformVisualizer';
import { pageTransition, staggerContainer, fadeUpItem } from '../hooks/useAnimation';
import type {
  Call,
  TranscriptSegment,
  ToolCall,
  AgentExecution,
  DashboardKPIs,
} from '../types';

// Demo data generators
function generateDemoCalls(): Call[] {
  const now = Date.now();
  return [
    {
      id: 'call-001',
      direction: 'inbound',
      status: 'live',
      participants: [
        { id: 'cust-1', name: 'Sarah Johnson', phoneNumber: '+44 7700 900123', type: 'customer' },
        { id: 'ai-1', name: 'AI Agent', phoneNumber: '', type: 'ai' },
      ],
      startTime: new Date(now - 120000).toISOString(),
      duration: 120,
      currentAgent: 'NetworkAgent',
      agentExecutionId: 'exec-001',
      transcriptId: 'trans-001',
      metadata: {
        customerId: 'C-78234',
        accountNumber: 'ACC-554321',
        reason: 'Slow Internet',
        satisfaction: 4.2,
        deflected: false,
      },
    },
    {
      id: 'call-002',
      direction: 'inbound',
      status: 'live',
      participants: [
        { id: 'cust-2', name: 'Michael Chen', phoneNumber: '+44 7700 900456', type: 'customer' },
        { id: 'ai-2', name: 'AI Agent', phoneNumber: '', type: 'ai' },
      ],
      startTime: new Date(now - 45000).toISOString(),
      duration: 45,
      currentAgent: 'AuthAgent',
      agentExecutionId: 'exec-002',
      transcriptId: 'trans-002',
      metadata: {
        customerId: 'C-89201',
        accountNumber: 'ACC-887654',
        reason: 'Billing Query',
        satisfaction: 4.5,
        deflected: true,
      },
    },
    {
      id: 'call-003',
      direction: 'inbound',
      status: 'ringing',
      participants: [
        { id: 'cust-3', name: 'Emma Williams', phoneNumber: '+44 7700 900789', type: 'customer' },
      ],
      startTime: new Date(now - 8000).toISOString(),
      duration: 8,
      metadata: {
        reason: 'New Connection',
      },
    },
    {
      id: 'call-004',
      direction: 'inbound',
      status: 'processing',
      participants: [
        { id: 'cust-4', name: 'James Brown', phoneNumber: '+44 7700 900321', type: 'customer' },
        { id: 'ai-4', name: 'AI Agent', phoneNumber: '', type: 'ai' },
      ],
      startTime: new Date(now - 300000).toISOString(),
      duration: 300,
      currentAgent: 'SupervisorAgent',
      agentExecutionId: 'exec-004',
      metadata: {
        customerId: 'C-44521',
        accountNumber: 'ACC-223456',
        reason: 'Service Outage',
        satisfaction: 3.8,
        deflected: false,
      },
    },
    {
      id: 'call-005',
      direction: 'inbound',
      status: 'hold',
      participants: [
        { id: 'cust-5', name: 'Olivia Davis', phoneNumber: '+44 7700 900654', type: 'customer' },
      ],
      startTime: new Date(now - 600000).toISOString(),
      endTime: new Date(now - 180000).toISOString(),
      duration: 420,
      metadata: {
        customerId: 'C-11234',
        reason: 'Technical Support',
        deflected: true,
      },
    },
  ];
}

function generateDemoTranscript(): TranscriptSegment[] {
  const now = Date.now();
  return [
    {
      id: 't1',
      callId: 'call-001',
      speaker: 'customer',
      text: 'Hi there, my internet has been really slow for the past two days. I work from home and this is causing serious problems.',
      timestamp: new Date(now - 120000).toISOString(),
      confidence: 0.94,
      isPartial: false,
    },
    {
      id: 't2',
      callId: 'call-001',
      speaker: 'ai',
      text: 'I completely understand how frustrating that must be, especially when working from home. I\'m going to run a diagnostic on your line right away to identify what\'s causing the slowdown.',
      timestamp: new Date(now - 115000).toISOString(),
      confidence: 0.98,
      isPartial: false,
    },
    {
      id: 't3',
      callId: 'call-001',
      speaker: 'system',
      text: 'Network diagnostic initiated for customer C-78234, line VDSL-554321',
      timestamp: new Date(now - 114000).toISOString(),
      confidence: 1,
      isPartial: false,
    },
    {
      id: 't4',
      callId: 'call-001',
      speaker: 'customer',
      text: 'Thank you. It\'s been dropping in and out as well, not just slow.',
      timestamp: new Date(now - 100000).toISOString(),
      confidence: 0.91,
      isPartial: false,
    },
    {
      id: 't5',
      callId: 'call-001',
      speaker: 'ai',
      text: 'That\'s very helpful information. Intermittent drops combined with speed reduction often point to a line stability issue or congestion in your area. The diagnostic will check both.',
      timestamp: new Date(now - 94000).toISOString(),
      confidence: 0.97,
      isPartial: false,
    },
    {
      id: 't6',
      callId: 'call-001',
      speaker: 'system',
      text: 'Line test complete: SNR margin 4.2dB (below 6dB threshold). DLM profile: Speed mode. 23 retrains in 24h.',
      timestamp: new Date(now - 85000).toISOString(),
      confidence: 1,
      isPartial: false,
    },
    {
      id: 't7',
      callId: 'call-001',
      speaker: 'ai',
      text: 'I\'ve found the issue. Your line has a noise margin below the stable threshold, which is causing the disconnections. I can see 23 reconnections in the last 24 hours. Let me apply a stability profile...',
      timestamp: new Date(now - 20000).toISOString(),
      confidence: 0.96,
      isPartial: false,
    },
    {
      id: 't8',
      callId: 'call-001',
      speaker: 'customer',
      text: 'That would be great, how long will that take?',
      timestamp: new Date(now - 5000).toISOString(),
      confidence: 0.92,
      isPartial: false,
    },
    {
      id: 't9',
      callId: 'call-001',
      speaker: 'ai',
      text: 'The stability profile is being applied now. This should take effect within the next 30 minutes. You may notice a slight speed reduction initially, but the connection will be much more stable.',
      timestamp: new Date(now - 2000).toISOString(),
      confidence: 0.95,
      isPartial: true,
    },
  ];
}

function generateDemoTools(): ToolCall[] {
  const now = Date.now();
  return [
    {
      id: 'tool-001',
      callId: 'call-001',
      toolName: 'authenticate_customer',
      params: { phoneNumber: '+447700900123', accountNumber: 'ACC-554321' },
      result: { authenticated: true, customerId: 'C-78234', tier: 'premium' },
      status: 'success',
      latency: 245,
      startTime: new Date(now - 120000).toISOString(),
      endTime: new Date(now - 119755).toISOString(),
      agentId: 'AuthAgent',
    },
    {
      id: 'tool-002',
      callId: 'call-001',
      toolName: 'get_account_details',
      params: { customerId: 'C-78234' },
      result: { plan: 'Fibre 900', monthlyCost: 45, contractEnd: '2025-06-01' },
      status: 'success',
      latency: 189,
      startTime: new Date(now - 119500).toISOString(),
      endTime: new Date(now - 119311).toISOString(),
      agentId: 'AuthAgent',
    },
    {
      id: 'tool-003',
      callId: 'call-001',
      toolName: 'run_line_diagnostic',
      params: { lineId: 'VDSL-554321', testType: 'full_spectrum' },
      result: {
        snrMargin: 4.2,
        attenuation: 12.5,
        syncSpeed: { up: 18900, down: 78900 },
        retrains: 23,
        errors: { es: 45, ses: 12 },
      },
      status: 'success',
      latency: 3400,
      startTime: new Date(now - 115000).toISOString(),
      endTime: new Date(now - 111600).toISOString(),
      agentId: 'NetworkAgent',
    },
    {
      id: 'tool-004',
      callId: 'call-001',
      toolName: 'check_area_status',
      params: { exchangeCode: 'EX-LDN-042', cabinetId: 'P12' },
      result: { incidents: 0, maintenance: false, congestionLevel: 'low' },
      status: 'success',
      latency: 520,
      startTime: new Date(now - 111000).toISOString(),
      endTime: new Date(now - 110480).toISOString(),
      agentId: 'NetworkAgent',
    },
    {
      id: 'tool-005',
      callId: 'call-001',
      toolName: 'apply_dlm_profile',
      params: { lineId: 'VDSL-554321', profile: 'stability' },
      status: 'running',
      latency: 1200,
      startTime: new Date(now - 20000).toISOString(),
      agentId: 'NetworkAgent',
    },
    {
      id: 'tool-006',
      callId: 'call-001',
      toolName: 'create_ticket',
      params: { customerId: 'C-78234', category: 'line_fault', priority: 'medium' },
      result: { ticketId: 'TKT-2024-7891', sla: '48h' },
      status: 'success',
      latency: 380,
      startTime: new Date(now - 15000).toISOString(),
      endTime: new Date(now - 14620).toISOString(),
      agentId: 'TicketAgent',
    },
    {
      id: 'tool-007',
      callId: 'call-001',
      toolName: 'schedule_engineer',
      params: { ticketId: 'TKT-2024-7891', earliest: '2024-01-15' },
      error: 'No engineer slots available before 2024-01-18',
      status: 'error',
      latency: 2100,
      startTime: new Date(now - 10000).toISOString(),
      endTime: new Date(now - 7900).toISOString(),
      agentId: 'TicketAgent',
    },
  ];
}

function generateDemoAgentExecution(): AgentExecution {
  return {
    id: 'exec-001',
    callId: 'call-001',
    nodes: [
      {
        id: 'auth',
        type: 'auth',
        name: 'AuthAgent',
        status: 'completed',
        latency: 245,
        startTime: new Date(Date.now() - 120000).toISOString(),
        endTime: new Date(Date.now() - 119755).toISOString(),
        output: { authenticated: true, customerId: 'C-78234', tier: 'premium' },
      },
      {
        id: 'network',
        type: 'network',
        name: 'NetworkAgent',
        status: 'running',
        latency: 5200,
        startTime: new Date(Date.now() - 119500).toISOString(),
        output: { snrMargin: 4.2, retrains: 23 },
      },
      {
        id: 'ticket',
        type: 'ticket',
        name: 'TicketAgent',
        status: 'completed',
        latency: 380,
        startTime: new Date(Date.now() - 15000).toISOString(),
        endTime: new Date(Date.now() - 14620).toISOString(),
        output: { ticketId: 'TKT-2024-7891' },
      },
      {
        id: 'supervisor',
        type: 'supervisor',
        name: 'SupervisorAgent',
        status: 'waiting',
        latency: 0,
      },
    ],
    connections: [
      { from: 'auth', to: 'network', status: 'completed' },
      { from: 'network', to: 'ticket', status: 'completed' },
      { from: 'ticket', to: 'supervisor', status: 'waiting' },
    ],
    startTime: new Date(Date.now() - 120000).toISOString(),
    status: 'running',
  };
}

export function Dashboard() {
  const navigate = useNavigate();
  const [calls] = useState<Call[]>(generateDemoCalls());
  const [transcript] = useState<TranscriptSegment[]>(generateDemoTranscript());
  const [tools] = useState<ToolCall[]>(generateDemoTools());
  const [agentExecution] = useState<AgentExecution>(generateDemoAgentExecution());
  const [selectedCallId, setSelectedCallId] = useState<string>('call-001');

  // Simulate audio data for waveform
  const [audioData] = useState(() => {
    const data = new Uint8Array(128);
    for (let i = 0; i < 128; i++) {
      data[i] = Math.random() * 100 + 30;
    }
    return data;
  });

  // Animate audio data
  useEffect(() => {
    const interval = setInterval(() => {
      for (let i = 0; i < 128; i++) {
        audioData[i] = Math.random() * 150 + 50;
      }
    }, 100);
    return () => clearInterval(interval);
  }, [audioData]);

  const kpis = useMemo<DashboardKPIs>(() => {
    const activeCalls = calls.filter((c) => c.status === 'live').length;
    const avgLatency = tools.reduce((sum, t) => sum + t.latency, 0) / (tools.filter((t) => t.latency > 0).length || 1);
    const deflected = calls.filter((c) => c.metadata.deflected).length;
    const totalActive = calls.filter((c) => ['live', 'processing', 'ringing'].includes(c.status)).length;

    return {
      activeCalls,
      avgLatency: Math.round(avgLatency),
      deflectionRate: totalActive > 0 ? Math.round((deflected / totalActive) * 100) : 0,
      satisfaction: 4.2,
      totalCallsToday: 142,
      avgDuration: 245,
      errorRate: 3,
      aiHandled: 128,
    };
  }, [calls, tools]);

  const handleSelectCall = (call: Call) => {
    setSelectedCallId(call.id);
    navigate(`/monitor?call=${call.id}`);
  };

  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="p-6 space-y-6"
    >
      {/* Page header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>
          <p className="text-sm text-text-muted mt-1">
            Real-time overview of AI Contact Center operations
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Activity className="w-4 h-4 text-success" />
          <span>All systems operational</span>
        </div>
      </motion.div>

      {/* KPI Cards Row */}
      <motion.div
        variants={staggerContainer(0.1)}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <KPICard
          title="Active Calls"
          value={kpis.activeCalls}
          icon={<Phone className="w-5 h-5" />}
          color="red"
          change={12}
          delay={0}
        />
        <KPICard
          title="Avg Latency"
          value={`${kpis.avgLatency}ms`}
          icon={<Zap className="w-5 h-5" />}
          color="orange"
          change={-8}
          delay={0.1}
        />
        <KPICard
          title="Deflection Rate"
          value={`${kpis.deflectionRate}%`}
          icon={<TrendingUp className="w-5 h-5" />}
          color="green"
          change={5}
          delay={0.2}
        />
        <KPICard
          title="Satisfaction"
          value={kpis.satisfaction.toFixed(1)}
          icon={<Smile className="w-5 h-5" />}
          color="blue"
          change={3}
          delay={0.3}
        />
      </motion.div>

      {/* Second KPI Row - smaller */}
      <motion.div
        variants={staggerContainer(0.08)}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {[
          { label: 'Total Calls Today', value: kpis.totalCallsToday, icon: Phone },
          { label: 'Avg Duration', value: `${Math.floor(kpis.avgDuration / 60)}m ${kpis.avgDuration % 60}s`, icon: Clock },
          { label: 'Error Rate', value: `${kpis.errorRate}%`, icon: AlertTriangle },
          { label: 'AI Handled', value: kpis.aiHandled, icon: Bot },
        ].map((item, i) => (
          <motion.div
            key={item.label}
            variants={fadeUpItem}
            className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]"
          >
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-text-muted">
              <item.icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-lg font-semibold text-text-primary tabular-nums">{item.value}</p>
              <p className="text-[10px] text-text-muted">{item.label}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column - Agent Graph + Waveform */}
        <div className="xl:col-span-2 space-y-6">
          {/* Waveform Visualizer */}
          <GlassCard variant="elevated" header={
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-vokentra" />
              <span className="text-sm font-semibold text-text-primary">Live Audio Stream</span>
              <span className="text-xs text-text-muted ml-2">call-001</span>
            </div>
          }>
            <WaveformVisualizer
              audioData={audioData}
              mode="bars"
              height={120}
              barCount={64}
              isActive={true}
            />
          </GlassCard>

          {/* Agent Execution Graph */}
          <GlassCard
            variant="elevated"
            className="min-h-[280px]"
            header={
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-vokentra" />
                  <span className="text-sm font-semibold text-text-primary">
                    Agent Execution Flow
                  </span>
                </div>
                <span className="text-xs text-text-muted font-mono">
                  {agentExecution.id}
                </span>
              </div>
            }
          >
            <AgentGraph
              execution={agentExecution}
              animated={true}
              className="w-full"
            />
          </GlassCard>

          {/* Live Transcript */}
          <GlassCard
            variant="default"
            className="min-h-[300px]"
            header={
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-success" />
                <span className="text-sm font-semibold text-text-primary">
                  Call Transcript
                </span>
                <span className="text-xs text-success">● Live</span>
              </div>
            }
          >
            <LiveTranscript segments={transcript} className="h-[350px]" />
          </GlassCard>
        </div>

        {/* Right column - Call Status + Tool Timeline */}
        <div className="space-y-6">
          {/* Active Calls */}
          <GlassCard
            variant="default"
            className="min-h-[400px]"
            header={
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-vokentra" />
                <span className="text-sm font-semibold text-text-primary">Active Calls</span>
              </div>
            }
          >
            <CallStatusPanel
              calls={calls}
              onSelectCall={handleSelectCall}
              selectedCallId={selectedCallId}
            />
          </GlassCard>

          {/* Tool Timeline */}
          <GlassCard
            variant="default"
            className="min-h-[400px]"
            header={
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-warning" />
                <span className="text-sm font-semibold text-text-primary">
                  MCP Tool Calls
                </span>
              </div>
            }
          >
            <ToolTimeline tools={tools} />
          </GlassCard>
        </div>
      </div>
    </motion.div>
  );
}
